import React, { useEffect } from 'react'

import classNames from 'classnames'

import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import { urlFor } from '~/components/atoms/Image/Image'
import ArticleHeader from '~/components/blocks/ArticleHeader/ArticleHeader'
import type { PageData } from '~/components/report/types'
import { resolveBlock } from '~/components/serializers'
import { ComponentTheme } from '~/components/themes'
import useMabPostHog, {
  PushInteractionLocation,
  PushInteractionType,
  PushWebProduct,
} from '~/hooks/useMabPostHog'
import { useZepic } from '~/hooks/useZepic'

import styles from '~/styles/pages.module.css'

/**
 * Renders a report using the article layout and styling, for reports flagged
 * with `simpleLayout` in Sanity. Deliberately omits the article-specific
 * furniture (skip-link menu, adverts, related articles, paywall fade-out) —
 * reports are gated wholesale by ReportGate before this ever renders.
 */
export default function SimpleReportLayout(props: PageData) {
  const {
    breadcrumb,
    title,
    subtitle,
    leadImage,
    primaryCategory,
    simpleBody,
    url,
  } = props

  const { pushEvent } = useMabPostHog()
  const { track: zepicTrack } = useZepic()

  // Mirrors PageBuilderLayout so simple-layout reports are counted the same way
  // in report-engagement dashboards and in Zepic.
  useEffect(() => {
    pushEvent('Online Report viewed', {
      interactionLocation: PushInteractionLocation.Reports,
      interactionType: PushInteractionType.PageLoad,
      webProduct: PushWebProduct.Reports,
    })

    if (leadImage) {
      zepicTrack('report_downloaded', {
        pageurl: `https://www.myartbroker.com/market-reports${url}`,
        title: title,
        image: urlFor(leadImage.asset, 800),
      })
    }
  }, [])

  // A report flagged `simpleLayout` with nothing in `simpleBody` would render
  // as a header over a blank page, so bail out rather than publish an empty
  // shell. Matches PDFDownloadLayout's `if (!pdfFile) return null`.
  if (!simpleBody || simpleBody.length === 0) return null

  // Reports have no article-type lozenge to fall back on, so an absent
  // category (or one with no translation for this locale) must render no
  // lozenge at all. Passing `undefined` rather than `null` matters: the `??`
  // in ArticleHeader treats null as "not supplied" and would derive an
  // /articles link instead.
  const lozenge = primaryCategory?.name
    ? {
        title: primaryCategory.name,
        link: `/market-reports?category=${primaryCategory.slug}#recent-reports`,
      }
    : undefined

  // A lead paragraph in first position renders full width above the body
  // column, matching the article layout, so it is pulled out here and skipped
  // in the module loop. Only that block is hoisted — a lead paragraph anywhere
  // else is an ordinary module and still renders inline.
  const hasLeadParagraph = simpleBody[0]._type === 'leadParagraph'

  return (
    <>
      <Breadcrumb list={breadcrumb} theme={ComponentTheme.primary} />

      <ArticleHeader
        title={title}
        subtitle={subtitle}
        image={leadImage}
        noPaddingBottom
        lozenge={lozenge}
        hideLozenge
      />

      {hasLeadParagraph && (
        <div className={styles.leadParagraphWrap}>
          <Container gutters={gutterTypes.always}>
            <div className={simpleBody[0]._type.toLowerCase()}>
              {resolveBlock(simpleBody[0], 0, ComponentTheme.magazine)}
            </div>
          </Container>
        </div>
      )}

      <div className={styles.articleBlocks}>
        <Container gutters={gutterTypes.always}>
          <div className={styles.mainArticleContent}>
            <div
              className={classNames(
                styles.articlesTextColumn,
                styles.simpleReportTextColumn
              )}
            >
              <div className={styles.articleModulesContainer}>
                {simpleBody.map((item, x) => {
                  if (x === 0 && hasLeadParagraph) return null

                  return (
                    <div
                      key={item._key || `simple-report-module-${x}`}
                      className={classNames(item._type?.toLowerCase())}
                    >
                      {resolveBlock(item, x, ComponentTheme.magazine)}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </Container>
      </div>
    </>
  )
}
